/* eslint-disable @stylistic/jsx-one-expression-per-line --
   散文中心の静的ヘルプページ。日本語の流し込み文中に inline <b>/<code> を混在させるのは
   意図的で可読性のため。全語を1行1要素に割る house style は本ページには非現実的なため、
   この整形ルールのみファイル単位で無効化 (correctness 系ルールは有効のまま)。 */

// CMS 操作・項目説明ガイド (2026-07-07 追加)。
// 運営 / 事業者が「絵柄・クーポン・事業者紐付け」の設定方法と各項目の意味を参照する静的ヘルプ。
// バックエンド / enduser 排出には一切影響しない (Phase B、表示専用ページ)。

const sectionTitle = 'text-[16px] font-bold mt-8 mb-3 border-l-4 border-active pl-2';
const th = 'text-left py-2 px-3 bg-gray-50 font-semibold text-gray-700 border-b border-gray-200';
const td = 'py-2 px-3 border-b border-gray-100 align-top';

export default function HelpPage() {
    return (
        <div className='layout-card'>
            <div className='max-w-3xl'>
                <h1 className='text-[20px] font-bold'>操作・項目説明ガイド</h1>
                <p className='text-sm text-gray-500 mt-1'>
                    絵柄・クーポン・事業者紐付けの設定方法と各項目の意味をまとめています。困った時の参照用です。
                </p>

                {/* 0. 全体像 */}
                <h2 className={sectionTitle}>1. 全体の仕組み</h2>
                <p className='text-sm leading-relaxed text-gray-700'>
                    omikuji は「ランドマークの前でスマホを振る → おみくじ(運勢+絵柄)が出て、クーポンが付いてくる」仕組みです。
                    表示は 3 つのレイヤーに分かれます。
                </p>
                <ul className='text-sm leading-relaxed text-gray-700 list-disc pl-5 mt-2 space-y-1'>
                    <li><b>絵柄・当たり構成</b> = 「テンプレートセット」+「テンプレートモチーフ」で決まる</li>
                    <li><b>クーポン</b> = 「クーポンテンプレート管理」で決まる(絵柄とは独立)</li>
                    <li><b>協賛絵柄</b> = 「スポンサー管理」→「協賛キャンペーン管理」で決まる(広告主向け・任意)</li>
                </ul>

                {/* 2. 絵柄を出す3ステップ */}
                <h2 className={sectionTitle}>2. ランドマークに「絵柄」を出す 3 ステップ</h2>
                <p className='text-sm leading-relaxed text-gray-700'>
                    テンプレートセットは、次の 3 つが揃って初めて enduser に絵柄が出ます。
                </p>
                <ol className='text-sm leading-relaxed text-gray-700 list-decimal pl-5 mt-2 space-y-1'>
                    <li><b>テンプレートセット管理</b> でセットを作る(tier プリセットは <b>5 段(おみくじ)</b>を選ぶ)</li>
                    <li><b>ランドマーク編集</b>の「omikuji テンプレートセット」欄でランドマークに割り当てる</li>
                    <li><b>テンプレートモチーフ管理</b>でそのセットに <b>絵柄を 5 枚(運勢 1〜5 各 1 枚)</b>入稿する</li>
                </ol>
                <p className='text-sm leading-relaxed text-gray-700 mt-2'>
                    絵柄を入れていない運勢は、既定の中立デザインのカード(地域特性のない和紙調の絵柄)にフォールバックします。
                    ※ 現在ランドマークに割り当てられるのは <b>5 段(おみくじ)プリセット</b>のセットのみです(ビンゴ/スタンプ等の非 5 段は対応準備中)。
                </p>

                {/* 3. テンプレートモチーフの項目 */}
                <h2 className={sectionTitle}>3. テンプレートモチーフ 新規作成の項目</h2>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm border-collapse mt-1'>
                        <thead>
                            <tr>
                                <th className={th}>項目</th>
                                <th className={th}>意味</th>
                                <th className={th}>設定方法 / 注意</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className={td}><b>画像 URL</b></td>
                                <td className={td}>enduser のカードに出る絵柄そのもの。</td>
                                <td className={td}>画像を先に公開 URL 化してから貼ります(<code>/postcards/xxx.png</code> または <code>https://…</code>)。<b>ファイルアップロード欄ではありません</b>。必須。</td>
                            </tr>
                            <tr>
                                <td className={td}><b>default tier value</b></td>
                                <td className={td}>どの運勢を引いた時にこの絵柄を出すか。1=大吉 / 2=中吉 / 3=小吉 / 4=末吉 / 5=凶。</td>
                                <td className={td}>1〜5 を入力。<b>5 枚そろえて 1 セット</b>。絵柄の出し分けはこの値で決まります(slug は無関係)。必須。</td>
                            </tr>
                            <tr>
                                <td className={td}><b>default 占い文</b></td>
                                <td className={td}>絵柄ごとの既定占い文用の欄。</td>
                                <td className={td}><b>現在は未使用</b>(占い文は別の「占い文プール」から選ばれます)。空欄で OK。</td>
                            </tr>
                            <tr>
                                <td className={td}><b>display_order</b></td>
                                <td className={td}>CMS 一覧での並び順のみ。</td>
                                <td className={td}>0,1,2… を入力。<b>抽選の絵柄選択には影響しません</b>(同じ運勢に複数あれば等確率ランダム)。</td>
                            </tr>
                            <tr>
                                <td className={td}><b>sponsor_purchase_disabled</b><br /><span className='text-xs text-gray-500'>(協賛化禁止)</span></td>
                                <td className={td}>この絵柄を「協賛枠として広告主に販売する」対象から外すフラグ。</td>
                                <td className={td}>自社店舗のセットは通常 OFF で OK。両替機 PoC の 5 種は ON(常設の自社資産なので売らない)。</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className='text-xs text-gray-500 mt-2'>
                    ※ 別項目の <b>postcard_image_url</b> は物理化(印刷)用で、enduser 表示には使いません(現在は空で OK)。
                    ※ <b>slug</b> はセット内でユニークな識別子。画像の出し分けは default tier value で行うため、slug が占い文と一致している必要はありません。
                </p>

                {/* 4. カード画角 */}
                <h2 className={sectionTitle}>4. カード入稿の画角(サイズ・レイアウト)</h2>
                <p className='text-sm leading-relaxed text-gray-700'>
                    実物のカードは <b>1024 × 1536 PNG(縦 2:3)</b>。新規カードもこれに合わせてください(形式は PNG または JPG)。
                    表示時に <b>上 20% に運勢ラベル・下 20% に占い文が重なって表示</b>されるため、その部分は「安全域」にします。
                </p>
                <div className='mt-3 flex gap-4 items-start flex-wrap'>
                    <div className='w-[180px] shrink-0'>
                        <div className='border-2 border-gray-400 rounded-md overflow-hidden' style={{ width: 180, height: 270 }}>
                            <div className='bg-amber-100 text-amber-800 text-[11px] flex items-center justify-center text-center px-1' style={{ height: '20%' }}>
                                上 20%<br />運勢ラベル安全域
                            </div>
                            <div className='bg-sky-100 text-sky-800 text-xs flex items-center justify-center text-center' style={{ height: '60%' }}>
                                主役(中央 60%)
                            </div>
                            <div className='bg-amber-100 text-amber-800 text-[11px] flex items-center justify-center text-center px-1' style={{ height: '20%' }}>
                                下 20%<br />占い文安全域
                            </div>
                        </div>
                        <p className='text-[11px] text-gray-500 text-center mt-1'>1024 × 1536(縦 2:3)</p>
                    </div>
                    <ul className='text-sm leading-relaxed text-gray-700 list-disc pl-5 space-y-1'>
                        <li><b>比率</b>: 縦 2:3(1024 × 1536 推奨)。表示は最大 360px 幅で縦横比そのまま。</li>
                        <li><b>主役</b>は中央 60% に配置。</li>
                        <li><b>上下 20%</b>は筆文字(白・金)が乗るので、明るい無地/薄い柄にして文字が読めるように。</li>
                        <li>柄を上下いっぱいに詰め込みすぎない。</li>
                    </ul>
                </div>

                {/* 5. クーポン */}
                <h2 className={sectionTitle}>5. クーポン設定のポイント</h2>
                <ul className='text-sm leading-relaxed text-gray-700 list-disc pl-5 space-y-1'>
                    <li><b>コード方式</b>: 統一コード(全員同じ)/個別コード(1 人 1 コード)。運用中に実績がつくと方式変更はロックされます。</li>
                    <li><b>配布上限 / 有効期間</b>: 上限に達する or 期間外になると配布停止。無期限は期間を空欄に。</li>
                    <li><b>有効化(is_active)</b>: OFF だと抽選対象外(=出ない)。公開直前に ON。</li>
                    <li><b>テンプレート名</b>は CMS 内部用で、enduser には出ません。enduser に出るのはコードと説明文です。</li>
                    <li><b>クーポンに画像を設定</b>すると、生き物カードの代わりにその画像が表示されます(運勢ラベル・占い文は残る)。</li>
                </ul>

                {/* 6. 事業者紐付け */}
                <h2 className={sectionTitle}>6. 事業者(BUSINESS)とランドマークの紐付け</h2>
                <ul className='text-sm leading-relaxed text-gray-700 list-disc pl-5 space-y-1'>
                    <li>事業者のログインアカウントは <b>ユーザー管理 → 事業者ユーザー作成</b>で発行します(スポンサー管理とは別)。</li>
                    <li>ランドマークとの紐付けは <b>ランドマーク編集の「スポンサー事業者」欄</b>(ドロップダウンで事業者名から選択)。</li>
                    <li>ユーザー管理一覧の <b>ID 列</b>が事業者の実 ID です(「No.」は表示上の連番で ID ではありません)。</li>
                </ul>

                {/* 7. 用語 */}
                <h2 className={sectionTitle}>7. 用語メモ</h2>
                <ul className='text-sm leading-relaxed text-gray-700 list-disc pl-5 space-y-1'>
                    <li><b>スポンサー管理</b> = おみくじの絵柄枠に協賛広告を出す機能(広告主登録→キャンペーン→絵柄)。店舗のクーポン運用とは別。</li>
                    <li><b>セット slug</b> = テンプレートセットの識別 ID。<b>モチーフ slug</b> = セット内の絵柄の識別 ID。どちらも英数字。</li>
                    <li><b>tier プリセット</b> = 当たりの段構成(おみくじ 5 段 / ビンゴ 2 段 等)。現在ランドマーク割当は 5 段のみ対応。</li>
                </ul>

                <p className='text-xs text-gray-400 mt-8'>最終更新: 2026-07-07</p>
            </div>
        </div>
    );
}
